import React from 'react';
import { createRoot } from 'react-dom/client';
import { Toolbar } from './components/Toolbar';
import { FlowCanvas } from './components/FlowCanvas';
import { useStore } from './store';
import { migrateLocalStorageData, needsMigration } from './utils/migration';
import { AuthProvider, LoginPage, useAuth } from './components/AuthProvider';

// Main app content (only shown when authenticated)
function AppContent() {
  return (
    <div className="app">
      <div id="toolbar">
        <Toolbar />
      </div>
      <div id="canvas">
        <FlowCanvas />
      </div>
    </div>
  );
}

// App wrapper with authentication check
function AppWithAuth() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  // Show main app if authenticated
  return <AppContent />;
}

// React wrapper component for the app
function App() {
  return (
    <AuthProvider>
      <AppWithAuth />
    </AuthProvider>
  );
}

export async function initApp() {
  const root = document.getElementById('root');
  if (!root) {
    throw new Error('Root element not found');
  }

  // Run migration if needed (before loading data)
  if (needsMigration()) {
    const result = migrateLocalStorageData();
    if (result.success) {
      console.log('[App] Migration successful:', result.message);
    } else {
      console.error('[App] Migration failed:', result.message);
    }
  }

  // Load from Base44/localStorage on init
  await useStore.getState().loadFromLocalStorage();

  // Mount React app
  const reactRoot = createRoot(root);
  reactRoot.render(<App />);

  // Set up auto-save to Base44
  setInterval(async () => {
    const state = useStore.getState();
    await state.saveToLocalStorage();
  }, 3000);
}
